import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import ErrorModal from "../components/ErrorModal";
import { parseBackendError, ParsedErrorInfo } from "../utils/errorHelper";

export interface ZamzamErrorContextType {
  showError: (error: any, fallbackMessage?: string, title?: string) => void;
  hideError: () => void;
}

const ZamzamErrorContext = createContext<ZamzamErrorContextType | undefined>(
  undefined
);

const GLOBAL_ERROR_EVENT = "zamzam:show-error";
const GLOBAL_HIDE_EVENT = "zamzam:hide-error";

export const ZamzamErrorProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [errorInfo, setErrorInfo] = useState<ParsedErrorInfo | null>(null);

  const showError = useCallback(
    (error: any, fallbackMessage?: string, customTitle?: string) => {
      const parsed = parseBackendError(error, fallbackMessage, customTitle);
      setErrorInfo(parsed);
      setIsOpen(true);
    },
    []
  );

  const hideError = useCallback(() => {
    setIsOpen(false);
    setErrorInfo(null);
  }, []);

  // Listen to global event so non-context or external callers can trigger modal without window.alert
  useEffect(() => {
    const handleGlobalShow = (e: any) => {
      if (e?.detail) {
        showError(e.detail.error, e.detail.fallbackMessage, e.detail.title);
      }
    };
    const handleGlobalHide = () => {
      hideError();
    };

    window.addEventListener(GLOBAL_ERROR_EVENT, handleGlobalShow);
    window.addEventListener(GLOBAL_HIDE_EVENT, handleGlobalHide);
    return () => {
      window.removeEventListener(GLOBAL_ERROR_EVENT, handleGlobalShow);
      window.removeEventListener(GLOBAL_HIDE_EVENT, handleGlobalHide);
    };
  }, [showError, hideError]);

  return (
    <ZamzamErrorContext.Provider value={{ showError, hideError }}>
      {children}
      <ErrorModal
        isOpen={isOpen}
        onClose={hideError}
        errorInfo={errorInfo}
      />
    </ZamzamErrorContext.Provider>
  );
};

export const useZamzamError = (): ZamzamErrorContextType => {
  const context = useContext(ZamzamErrorContext);
  if (!context) {
    return {
      showError: (error: any, fallbackMessage?: string, title?: string) => {
        // Dispatches to provider event without ever using native browser alert
        window.dispatchEvent(
          new CustomEvent(GLOBAL_ERROR_EVENT, {
            detail: { error, fallbackMessage, title },
          })
        );
      },
      hideError: () => {
        window.dispatchEvent(new CustomEvent(GLOBAL_HIDE_EVENT));
      },
    };
  }
  return context;
};
